import React, { useState } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Save } from 'lucide-react';
import { Layout } from '../../components/Layout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { PracticeMainFields } from '../../components/training/practice/PracticeMainFields';
import { PracticeStatusFields } from '../../components/training/practice/PracticeStatusFields';
import { PracticeHistory } from '../../components/training/practice/PracticeHistory';
import { PracticeScheduling } from '../../components/training/practice/PracticeScheduling';
import { PracticeBillingFields } from '../../components/training/practice/PracticeBillingFields';
import { ProspectConfirmModal } from '../../components/training/practice/ProspectConfirmModal';
import { usePractices } from '../../hooks/usePractices';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { practicesStore } from '../../services/practicesStore';
import { mockData } from '../../services/mockData';
import { duplicatePractice, emptyPracticeDraft, isInPresence, requesterName } from '../../utils/practices';
import { PracticeDraft, PracticeErrors, PracticeStatus } from '../../types/practice';
export function PracticeEditorPage() {
  const {
    practiceId
  } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    practices
  } = usePractices();
  const courses = useTrainingCourses();
  const existing = practiceId ? practices.find(p => p.id === practiceId) : undefined;
  const source = practices.find(p => p.id === searchParams.get('duplica'));
  const [draft, setDraft] = useState<PracticeDraft>(() => existing ? {
    ...existing
  } : source ? duplicatePractice(source) : emptyPracticeDraft());
  const [errors, setErrors] = useState<PracticeErrors>({});
  const [prospectName, setProspectName] = useState<string | null>(null);
  const [activatedNotice, setActivatedNotice] = useState('');
  if (practiceId && !existing) return <Navigate to="/training/pratiche" replace data-id="element-6302" data-mp="351mg94vhkkrg" />;
  const update = (changes: Partial<PracticeDraft>) => {
    setDraft(prev => ({
      ...prev,
      ...changes
    }));
    setErrors(prev => {
      const next = {
        ...prev
      };
      Object.keys(changes).forEach(k => delete next[k]);
      if ('participants' in changes) delete next.credentials;
      if ('clientType' in changes) {
        delete next.companyName;
        delete next.firstName;
        delete next.lastName;
        delete next.businessName;
      }
      return next;
    });
  };
  const handleStatus = (status: PracticeStatus) => {
    // Positivo: the requested course becomes the assigned course
    const requested = status === 'Positivo' && draft.requestedCourseCode && !draft.courseCode ? courses.find(c => c.courseCode === draft.requestedCourseCode) : undefined;
    update({
      status,
      ...(status === 'Positivo' && draft.requestedCourseCode && !draft.courseCode ? {
        courseCode: draft.requestedCourseCode,
        ...(requested?.status === 'ondemand' ? {
          deliveryMode: 'elearning' as const,
          courseStatus: 'On-demand' as const
        } : {})
      } : {})
    });
    // Positive outcome on a prospect client: ask to promote it to active
    if (status === 'Positivo' && draft.clientType === 'anagrafica' && draft.companyName && mockData.getClientByName(draft.companyName)?.status === 'Prospect') {
      setProspectName(draft.companyName);
    }
  };
  const activateClient = () => {
    if (!prospectName) return;
    mockData.setClientStatus(prospectName, 'Attivo');
    setActivatedNotice(`${prospectName} è ora un cliente attivo.`);
    setProspectName(null);
  };
  const validateScheduling = (): PracticeErrors => {
    const next: PracticeErrors = {};
    if (!draft.deliveryMode) {
      next.deliveryMode = 'Scegli il tipo di corso';
      return next;
    }
    if (!draft.courseCode) next.courseCode = 'Inserisci il codice corso';
    if (isInPresence(draft)) {
      if (!draft.courseStartDate) next.courseStartDate = 'Calendarizza il corso';
      if (draft.courseStartDate && draft.courseEndDate && draft.courseEndDate < draft.courseStartDate) next.courseEndDate = 'La data di fine non può precedere la data di inizio';
    } else {
      const valid = draft.participants.filter(p => p.firstName.trim() && p.lastName.trim() && p.email.trim());
      if (valid.length === 0 || valid.length !== draft.participants.length) next.participants = 'Inserisci nome, cognome ed email di ogni partecipante';else if (draft.participants.some(p => !p.credentialsSentAt)) next.credentials = 'Genera e invia le credenziali di accesso';
    }
    return next;
  };
  const confirmScheduling = () => {
    const next = validateScheduling();
    setErrors(prev => ({
      ...prev,
      ...next
    }));
    if (Object.keys(next).length === 0) update({
      schedulingConfirmed: true
    });
  };
  const validate = (): PracticeErrors => {
    const next: PracticeErrors = {};
    if (draft.clientType === 'anagrafica' && !draft.companyName) next.companyName = 'Seleziona la denominazione aziendale';
    if (draft.clientType === 'individuo') {
      if (!draft.firstName?.trim()) next.firstName = 'Inserisci il nome';
      if (!draft.lastName?.trim()) next.lastName = 'Inserisci il cognome';
    }
    if (draft.clientType === 'occasionale' && !draft.businessName?.trim()) next.businessName = 'Inserisci la ragione sociale';
    if (!draft.category) next.category = 'Seleziona la macrocategoria';
    if (!draft.productCode) next.productCode = 'Seleziona codice prodotto e tipologia corso';
    if (draft.status === 'Negativo') {
      if (!draft.rejectionDate) next.rejectionDate = 'Inserisci la data della non accettazione';
      if (!draft.rejectionReason?.trim()) next.rejectionReason = 'Inserisci la motivazione';
    }
    return next;
  };
  const handleSave = () => {
    const next = validate();
    setErrors(next);
    if (Object.keys(next).length > 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    const isPositive = draft.status === 'Positivo';
    const saved = practicesStore.save({
      ...draft,
      // Keep only the data that applies to the chosen requester and status
      companyName: draft.clientType === 'anagrafica' ? draft.companyName : undefined,
      firstName: draft.clientType === 'individuo' ? draft.firstName : undefined,
      lastName: draft.clientType === 'individuo' ? draft.lastName : undefined,
      businessName: draft.clientType === 'occasionale' ? draft.businessName : undefined,
      // Individuals attend themselves: no attendee list
      attendees: draft.clientType === 'individuo' ? [] : draft.attendees || [],
      rejectionDate: draft.status === 'Negativo' ? draft.rejectionDate : undefined,
      rejectionReason: draft.status === 'Negativo' ? draft.rejectionReason : undefined,
      schedulingConfirmed: isPositive && draft.schedulingConfirmed,
      // Keep only the inputs of the chosen rate type
      hourlyRate: draft.rateType === 'oraria' ? draft.hourlyRate : undefined,
      courseHours: draft.rateType === 'oraria' ? draft.courseHours : undefined,
      singleFee: draft.rateType === 'singola' ? draft.singleFee : undefined
    });
    navigate('/training/pratiche', {
      state: {
        saved: existing ? 'updated' : 'created',
        id: saved.id
      }
    });
  };
  const errorCount = Object.keys(errors).length;
  const title = existing ? 'Modifica pratica' : source ? 'Duplica pratica' : 'Nuova pratica';
  return <Layout data-id="element-6303" data-mp="1eu53gjy8132r">
      <Link to="/training/pratiche" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-seform-navy mb-4" data-id="element-6304" data-mp="1eueved2ttkb3">
        <ArrowLeft className="h-4 w-4" data-id="element-6305" data-mp="2alxofs42tds3" /> Pratiche
      </Link>
      <div className="mb-6" data-id="element-6306" data-mp="25lrxyf9jlxbl">
        <h1 className="text-3xl font-bold text-seform-navy" data-id="element-6307" data-mp="25kqilylhuoo2">{title}</h1>
        {(existing || source) && <p className="text-slate-500 mt-1" data-id="element-6308" data-mp="21kzkf9tn169i">
            {source && !existing ? 'Copia di: ' : ''}
            {requesterName(existing || source!)}
          </p>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-6309" data-mp="2tbm82p2h97s5">
        <div className="lg:col-span-2 space-y-6" data-id="element-6310" data-mp="2pa0xjadf15l4">
          <Card noHoverShadow data-id="element-6311" data-mp="2paaqn6trbsv1">
            <PracticeMainFields draft={draft} onChange={update} errors={errors} data-id="element-6312" data-mp="2pc51ttf886lx" />
            <div className="pt-6 border-t border-slate-100" data-id="element-6313" data-mp="2kh7qgfuh5l5v">
              <PracticeStatusFields draft={draft} onStatusChange={handleStatus} onChange={update} errors={errors} data-id="element-6314" data-mp="2fiwk4f3e1sse" />
              {activatedNotice && <p role="status" className="text-sm text-green-700 mt-3" data-id="element-6315" data-mp="387bxkx6vg31m">
                  {activatedNotice}
                </p>}
            </div>
          </Card>

          {draft.status === 'Positivo' && <>
              <Card title="Processo programmazione corso" noHoverShadow data-id="element-6316" data-mp="3pi7gmic2hsrb">
                <PracticeScheduling draft={draft} onChange={update} errors={errors} onConfirm={confirmScheduling} data-id="element-6317" data-mp="3pkbrxlcapu1m" />
              </Card>
              <Card noHoverShadow data-id="element-6318" data-mp="7c2pjy3jr9ue">
                <PracticeBillingFields draft={draft} onChange={update} data-id="element-6319" data-mp="7b1fnwy9ote9" />
              </Card>
            </>}
        </div>

        <aside className="lg:sticky lg:top-6 space-y-4" data-id="element-6320" data-mp="c55ludzpkxea">
          <Card title="Storico e azioni" noHoverShadow data-id="element-6321" data-mp="h147httnk1b0">
            <PracticeHistory history={draft.history} onAdd={entry => update({
            history: [...draft.history, entry]
          })} data-id="element-6322" data-mp="l3r5kcgx2ign" />
          </Card>
          {errorCount > 0 && <div role="alert" className="flex gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3" data-id="element-6323" data-mp="3ozxhrpnb0e3b">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" data-id="element-6324" data-mp="3tza0by756z03" />
              <p className="text-sm text-red-700" data-id="element-6325" data-mp="2rfqsxmpwrhj">
                {errorCount === 1 ? 'C’è 1 campo da correggere.' : `Ci sono ${errorCount} campi da correggere.`}
              </p>
            </div>}
          <div className="flex gap-3" data-id="element-6326" data-mp="bn57itd80xol">
            <Button variant="flat" color="default" className="flex-1" onClick={() => navigate('/training/pratiche')} data-id="element-6327" data-mp="gj2on1wb682f">
              Annulla
            </Button>
            <Button className="flex-1" onClick={handleSave} startContent={<Save className="h-4 w-4" data-id="element-6329" data-mp="2nbwfmgc3pxv0" />} data-id="element-6328" data-mp="qhjiq7qjn2sp">
              {existing ? 'Salva modifiche' : 'Crea pratica'}
            </Button>
          </div>
        </aside>
      </div>

      <ProspectConfirmModal companyName={prospectName} onKeep={() => setProspectName(null)} onActivate={activateClient} data-id="element-6330" data-mp="19fbt7dhtqo85" />
    </Layout>;
}